import { useMergedRefs, useResizeObserver } from '@ardoq/hooks';
import { isTextTruncated } from '@ardoq/common-helpers';
import { s4 } from '@ardoq/design-tokens';
import {
  HTMLAttributes,
  Ref,
  useCallback,
  useLayoutEffect,
  useRef,
} from 'react';
import styled, { css } from 'styled-components';

type TextOverflowEllipsisProps = HTMLAttributes<HTMLDivElement> & {
  onEllipsisChange: (isTextOverflowed: boolean) => void;
  onInnerTextChange: (innerText: string) => void;
  lineClamp?: number;
  focusable?: boolean;
  showWhitespace?: boolean;
  ref?: Ref<HTMLDivElement>;
};

export const WrapperWithEllipsisOverflow = styled.div<{
  $lineClamp?: number;
  $focusable?: boolean;
  $showWhitespace?: boolean;
}>`
  white-space: nowrap;
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  /* If focusable, 4px padding is needed to display the focus ring */
  padding: ${props => (props.$focusable ? s4 : 0)};

  ${props =>
    props.$lineClamp &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: ${props.$lineClamp};
      white-space: initial;
      overflow-wrap: break-word;
      -webkit-box-orient: vertical;
    `}

  ${props =>
    props.$showWhitespace &&
    css`
      /** Preserve white space */
      white-space: pre !important;
    `}
`;

/**
 * Wrapper component providing truncation with ellipsis for your content.
 * @component
 */
export const TextOverflowEllipsis = ({
  className,
  children,
  onEllipsisChange,
  onInnerTextChange,
  lineClamp,
  focusable,
  showWhitespace,
  ref: forwardedRef,
  ...rest
}: TextOverflowEllipsisProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const setRefs = useMergedRefs(ref, forwardedRef);
  const measuredText = useRef<string | null>(null);
  const recalculateOverflow = useCallback(() => {
    const wrapperEl = ref.current;
    if (!wrapperEl) {
      return;
    }

    const innerText = wrapperEl.textContent ?? '';
    measuredText.current = innerText;
    onInnerTextChange(innerText);
    onEllipsisChange(isTextTruncated(wrapperEl, lineClamp));
  }, [lineClamp, onEllipsisChange, onInnerTextChange]);

  useLayoutEffect(recalculateOverflow, [recalculateOverflow]);
  useLayoutEffect(() => {
    if (ref.current?.textContent === measuredText.current) {
      return;
    }
    recalculateOverflow();
  }, [recalculateOverflow, children]);
  useResizeObserver(ref, recalculateOverflow);

  return (
    <WrapperWithEllipsisOverflow
      ref={setRefs}
      className={className}
      $lineClamp={lineClamp}
      $focusable={focusable}
      $showWhitespace={showWhitespace}
      {...rest}
    >
      {children}
    </WrapperWithEllipsisOverflow>
  );
};
